import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, UploadCloud, Eye, CheckCircle2, XCircle, AlertCircle, Clock, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    PENDING_UPLOAD: "bg-gray-100 text-gray-700 border-gray-200",
    UPLOADED:       "bg-blue-50 text-blue-700 border-blue-200",
    RE_UPLOADED:    "bg-blue-50 text-blue-700 border-blue-200",
    UNDER_REVIEW:   "bg-amber-50 text-amber-700 border-amber-200",
    VERIFIED:       "bg-emerald-50 text-emerald-700 border-emerald-200",
    NEEDS_CORRECTION: "bg-red-50 text-red-700 border-red-200",
    DENIED:         "bg-red-100 text-red-800 border-red-300",
};

const STATUS_ICON = {
    PENDING_UPLOAD: Clock, UPLOADED: FileText, RE_UPLOADED: FileText,
    UNDER_REVIEW: AlertCircle, VERIFIED: CheckCircle2,
    NEEDS_CORRECTION: AlertCircle, DENIED: XCircle,
};

function prettyType(t) { return t.replace(/_/g, " "); }

export default function LoanDocuments() {
    const { id } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [docs, setDocs] = useState([]);
    const [completion, setCompletion] = useState(null);
    const [viewer, setViewer] = useState(null); // {doc, blobUrl}
    const [reviewer, setReviewer] = useState(null); // {doc, notes}

    const canUpload = ["ceo", "office_admin", "marketing_executive", "agent", "branch_manager"].includes(user.role);
    const canVerify = ["ceo", "office_admin"].includes(user.role);
    const canReview = user.role === "office_admin" || user.role === "ceo";

    const load = useCallback(async () => {
        try {
            const [r, d, c] = await Promise.all([
                api.get(`/loans/${id}/document-requirements`),
                api.get(`/loans/${id}/documents`),
                api.get(`/loans/${id}/document-completion`),
            ]);
            setData(r.data); setDocs(d.data); setCompletion(c.data);
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const openDoc = async (docId) => {
        try {
            const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const doc = docs.find((x) => x.id === docId);
            setViewer({ doc, blobUrl: url });
        } catch (err) { toast.error("Cannot open document"); }
    };

    const closeViewer = () => {
        if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl);
        setViewer(null);
    };

    const openReview = (doc) => setReviewer({ doc, notes: doc.review_notes || "" });

    const submitReview = async (status) => {
        try {
            await api.patch(`/documents/${reviewer.doc.id}/review`, {
                status, review_notes: reviewer.notes || null,
            });
            toast.success(`Marked ${status}`);
            setReviewer(null);
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    if (!data) return <div className="text-gray-500">Loading checklist…</div>;

    return (
        <div className="space-y-6" data-testid="loan-documents-page">
            <Link to={`/loans/${id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-komma-navy hover:text-komma-gold" data-testid="back-loan">
                <ChevronLeft size={14} /> Back to loan
            </Link>

            <div className="komma-card p-8 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-komma-gold/10" />
                <p className="text-[10px] uppercase tracking-[0.3em] text-komma-gold">Document Checklist</p>
                <h1 className="font-serif text-3xl text-komma-navy mt-2">{data.loan.customer_name}</h1>
                <p className="text-sm text-gray-600 capitalize">{data.loan.loan_type} loan · {data.loan.loan_number}</p>
                {completion && (
                    <>
                        <div className="komma-gold-hairline my-6" />
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            {[["Required", completion.total, "text-komma-navy"],
                              ["Verified", completion.verified, "text-emerald-700"],
                              ["Under Review", completion.under_review, "text-amber-700"],
                              ["Needs Correction", completion.needs_correction, "text-red-700"],
                              ["Denied", completion.denied, "text-red-800"],
                              ["Pending", completion.pending, "text-gray-500"]].map(([label, v, c]) => (
                                <div key={label}>
                                    <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
                                    <p className={`font-serif text-2xl mt-1 ${c}`}>{v}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4">
                            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                <div className="h-full bg-komma-gold" style={{ width: `${completion.progress_pct}%` }} />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {completion.progress_pct}% complete
                                {completion.ready_for_disbursal && <span className="ml-3 text-emerald-700 font-semibold">✓ Ready for disbursal</span>}
                            </p>
                        </div>
                    </>
                )}
            </div>

            <div className="space-y-3" data-testid="checklist-list">
                {data.requirements.map((r) => {
                    const currentDoc = docs.find((d) => d.doc_type === r.doc_type && d.version === r.current_version);
                    const versions = docs.filter((d) => d.doc_type === r.doc_type).sort((a, b) => b.version - a.version);
                    const Icon = STATUS_ICON[r.status] || Clock;
                    return (
                        <div key={r.id} className="komma-card p-5" data-testid={`req-${r.doc_type}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <Icon size={18} className="text-komma-gold" />
                                        <p className="font-serif text-lg text-komma-navy capitalize">{prettyType(r.doc_type)}</p>
                                        <Badge variant="outline" className="text-[10px] uppercase tracking-widest">Required</Badge>
                                        <Badge variant="outline" className={`${STATUS_STYLES[r.status] || ""} text-[10px] uppercase tracking-widest`}>{r.status.replace("_", " ")}</Badge>
                                    </div>
                                    {currentDoc && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            v{currentDoc.version} · uploaded by <span className="text-komma-navy">{currentDoc.uploaded_by_name}</span> · {new Date(currentDoc.created_at).toLocaleString()}
                                        </p>
                                    )}
                                    {r.review_notes && (
                                        <div className="mt-2 p-3 rounded-md bg-red-50 border border-red-100">
                                            <p className="text-[10px] uppercase tracking-widest text-red-700">Reviewer notes</p>
                                            <p className="text-xs text-red-800 mt-1 italic">&ldquo;{r.review_notes}&rdquo;</p>
                                        </div>
                                    )}
                                    {r.verified_at && (
                                        <p className="text-xs text-emerald-700 mt-2">Verified by {r.verified_by} on {new Date(r.verified_at).toLocaleString()}</p>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                    {currentDoc && (
                                        <Button size="sm" variant="outline" onClick={() => openDoc(currentDoc.id)}
                                            className="border-komma-navy/30 text-komma-navy" data-testid={`view-${r.doc_type}`}>
                                            <Eye size={12} className="mr-1" /> View v{currentDoc.version}
                                        </Button>
                                    )}
                                    {canUpload && (
                                        <UploadButton loanId={id} docType={r.doc_type} onDone={load}
                                            reupload={r.status === "NEEDS_CORRECTION" || r.status === "DENIED"} />
                                    )}
                                    {canVerify && currentDoc && ["UPLOADED", "RE_UPLOADED", "UNDER_REVIEW"].includes(r.status) && (
                                        <Button size="sm" onClick={() => openReview(currentDoc)} className="bg-komma-gold hover:bg-komma-gold-dark text-komma-navy"
                                            data-testid={`review-${r.doc_type}`}>
                                            Review
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {versions.length > 1 && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Version history</p>
                                    <div className="flex flex-wrap gap-2">
                                        {versions.map((v) => (
                                            <button key={v.id} onClick={() => openDoc(v.id)}
                                                className="text-xs px-3 py-1 rounded-full border border-gray-200 hover:border-komma-gold hover:bg-komma-gold/10 transition-colors">
                                                v{v.version} · {v.status.replace("_", " ").toLowerCase()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Viewer */}
            <Dialog open={!!viewer} onOpenChange={(v) => !v && closeViewer()}>
                <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-komma-navy">
                            {viewer && `${prettyType(viewer.doc.doc_type)} · v${viewer.doc.version}`}
                        </DialogTitle>
                    </DialogHeader>
                    {viewer && (
                        <div className="flex-1 overflow-auto rounded-md border border-gray-200 bg-gray-50">
                            {viewer.doc.mime?.startsWith("image/") ? (
                                <img src={viewer.blobUrl} alt="" className="w-full h-full object-contain" />
                            ) : (
                                <iframe title="doc" src={viewer.blobUrl} className="w-full h-full" />
                            )}
                        </div>
                    )}
                    {viewer?.doc.annotations?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto text-xs">
                            <p className="uppercase tracking-widest text-komma-gold text-[10px]">Annotations by {viewer.doc.reviewed_by_name}</p>
                            <ul className="mt-2 space-y-1">
                                {viewer.doc.annotations.map((a, i) => (
                                    <li key={i} className="text-gray-700">
                                        <span className="uppercase text-[10px] text-komma-gold mr-2">{a.kind}</span>
                                        Page {a.page || 1} — {a.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reviewer */}
            <Dialog open={!!reviewer} onOpenChange={(v) => !v && setReviewer(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-komma-navy">
                            Review {reviewer && prettyType(reviewer.doc.doc_type)}
                        </DialogTitle>
                    </DialogHeader>
                    <Textarea placeholder="Review notes (required for 'Needs correction')"
                        value={reviewer?.notes || ""} onChange={(e) => setReviewer({ ...reviewer, notes: e.target.value })}
                        rows={5} data-testid="review-notes" />
                    <div className="grid grid-cols-3 gap-2">
                        <Button onClick={() => submitReview("VERIFIED")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="verify-btn">
                            <CheckCircle2 size={14} className="mr-1" /> Verify
                        </Button>
                        <Button onClick={() => submitReview("NEEDS_CORRECTION")} variant="outline" className="border-amber-500 text-amber-700" data-testid="needs-correction-btn">
                            <AlertCircle size={14} className="mr-1" /> Correct
                        </Button>
                        <Button onClick={() => submitReview("DENIED")} variant="outline" className="border-red-500 text-red-700" data-testid="deny-btn">
                            <XCircle size={14} className="mr-1" /> Deny
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function UploadButton({ loanId, docType, onDone, reupload }) {
    const inputRef = useRef();
    const [busy, setBusy] = useState(false);
    const submit = async (f) => {
        if (!f) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", docType);
            fd.append("file", f);
            await api.post(`/loans/${loanId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${reupload ? "Re-uploaded" : "Uploaded"} ${docType.replace("_", " ")}`);
            onDone && onDone();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
        finally { setBusy(false); inputRef.current.value = ""; }
    };
    return (
        <>
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy}
                className={reupload ? "bg-red-600 hover:bg-red-700" : "bg-komma-navy hover:bg-komma-navy-light"}
                data-testid={`upload-${docType}`}>
                <UploadCloud size={12} className="mr-1" /> {busy ? "…" : reupload ? "Re-upload" : "Upload"}
            </Button>
            <input ref={inputRef} type="file" accept="application/pdf,image/*" hidden
                onChange={(e) => submit(e.target.files?.[0])} />
        </>
    );
}
